import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';

import Modal from 'react-bootstrap/Modal';

import Button from 'react-bootstrap/Button';

import { Box, CircularProgress, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import {
  fetchNotificationDetail,
  useReadNotificationsMutation,
  type NotificationDetailView,
} from '@/apis/service/notifications';

import AnnouncementNotificationBody from './AnnouncementNotificationBody';

import StandardNotificationBody from './StandardNotificationBody';

export type ModalBody = {
  /** Notification row id — used for GET detail and mark-as-read. */

  id: string;

  hasFooter?: boolean;
};

interface ModalContextType {
  onShow: (bodyContent?: ModalBody) => void;

  onHide: () => void;

  onSubmit: () => void;
}

const ModalContext = createContext<ModalContextType>({
  onHide: () => {},

  onShow: () => {},

  onSubmit: () => {},
});

const ModalProvider: FC = ({ children }) => {
  const { t } = useTranslation();

  const [visible, setVisible] = useState<boolean>(false);

  const { mutate } = useReadNotificationsMutation();

  const [body, setBody] = useState<ModalBody>({
    id: '0',

    hasFooter: true,
  });

  const [detail, setDetail] = useState<NotificationDetailView | null>(null);

  const [detailLoading, setDetailLoading] = useState(false);

  const [detailError, setDetailError] = useState<string | null>(null);

  const resetDetailState = useCallback(() => {
    setDetail(null);

    setDetailLoading(false);

    setDetailError(null);
  }, []);

  const onShow = useCallback(
    (bodyContent?: ModalBody) => {
      if (bodyContent) setBody(bodyContent);

      resetDetailState();

      setVisible(true);
    },

    [resetDetailState],
  );

  const onHide = useCallback(() => {
    setVisible(false);

    resetDetailState();
  }, [resetDetailState]);

  const onSubmit = useCallback(() => {
    mutate({
      list_notification_id: [body.id],
    });

    setVisible(false);

    resetDetailState();
  }, [body.id, mutate, resetDetailState]);

  useEffect(() => {
    if (!visible || !body.id) return;

    let cancelled = false;

    setDetailLoading(true);

    setDetailError(null);

    setDetail(null);

    void fetchNotificationDetail(body.id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })

      .catch(() => {
        if (!cancelled) setDetailError(t('notifications.detail_error'));
      })

      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, body.id, t]);

  const modalTitle = detail?.title?.trim() ?? '';

  const hasFooter = body.hasFooter !== false;

  return (
    <ModalContext.Provider
      value={{
        onHide,

        onShow,

        onSubmit,
      }}>
      {children}

      <Modal size="lg" centered scrollable backdrop="static" show={visible} onHide={onHide}>
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {detailLoading ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', minHeight: 28 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              modalTitle
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Typography color="error" variant="body2">
              {detailError}
            </Typography>
          ) : detail?.kind === 'announcement' && detail.announcement ? (
            <AnnouncementNotificationBody detail={detail.announcement} />
          ) : detail?.kind === 'standard' ? (
            <StandardNotificationBody detail={detail} />
          ) : null}
        </Modal.Body>

        {hasFooter && (
          <Modal.Footer>
            <Button
              form={'hook-form'}
              size="sm"
              variant="primary"
              type="submit"
              disabled={detailLoading || !detail}
              onClick={onSubmit}>
              Ok
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </ModalContext.Provider>
  );
};

export default ModalProvider;

export const useModalContext = () => useContext(ModalContext);
